(() => {
  const loader =
    document.currentScript ||
    document.querySelector("script[data-color-swatch-script]");
  const enhancementScripts = [
    loader?.dataset.colorSwatchScript,
    loader?.dataset.cardMediaScript,
  ].filter(Boolean);
  let readyPromise;
  let pendingSwatchTarget = null;

  const hasCardsToEnhance = () =>
    document.querySelector("color-swatch, [data-card-media-track]");

  const isScriptAlreadyLoaded = (script) => {
    if (script.dataset.loaderReady === "true") return true;
    if (
      script.readyState === "complete" ||
      script.readyState === "loaded"
    ) {
      return true;
    }
    return performance
      .getEntriesByName(script.src, "resource")
      .some((entry) => entry.responseEnd > 0);
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existingScript = Array.from(document.scripts).find(
        (script) => script.src === src,
      );

      if (existingScript) {
        if (isScriptAlreadyLoaded(existingScript)) {
          existingScript.dataset.loaderReady = "true";
          resolve();
          return;
        }

        existingScript.addEventListener(
          "load",
          () => {
            existingScript.dataset.loaderReady = "true";
            resolve();
          },
          { once: true },
        );
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = false;
      script.addEventListener(
        "load",
        () => {
          script.dataset.loaderReady = "true";
          resolve();
        },
        { once: true },
      );
      script.addEventListener("error", reject, { once: true });
      document.head.appendChild(script);
    });

  const loadCardEnhancements = () => {
    if (!hasCardsToEnhance() || !enhancementScripts.length)
      return Promise.resolve();
    if (readyPromise) return readyPromise;

    readyPromise = enhancementScripts
      .reduce((promise, src) => promise.then(() => loadScript(src)), Promise.resolve())
      .catch((error) => {
        readyPromise = null;
        throw error;
      });
    return readyPromise;
  };

  window.loadCardEnhancements = loadCardEnhancements;

  // Keep selected swatch first + visible count consistent before the full
  // color-swatch.js bundle loads (mobile has no hover warm-up).
  const promoteCardSwatches = () => {
    document
      .querySelectorAll("color-swatch .card__color-swatcher--container")
      .forEach((container) => {
        const host = container.closest("color-swatch");
        const limit =
          parseInt(host?.dataset?.swatchLimit || "4", 10) || 4;
        const active = container.querySelector(
          ".color-swatcher--wrapper.active",
        );
        if (active && container.firstElementChild !== active) {
          container.insertBefore(active, container.firstElementChild);
        }

        const wrappers = container.querySelectorAll(
          ".color-swatcher--wrapper",
        );
        wrappers.forEach((wrapper, index) => {
          wrapper.classList.toggle("hidden", index >= limit);
        });

        const counter = container.querySelector(".color-swatcher--counter");
        if (!counter) return;
        const hiddenCount = wrappers.length - limit;
        if (hiddenCount > 0) {
          counter.textContent = `+ ${hiddenCount}`;
          counter.classList.remove("hidden");
        } else {
          counter.textContent = "";
          counter.classList.add("hidden");
        }
      });
  };

  const loadFromCardInteraction = (event) => {
    if (event.target.closest(".card, color-swatch, [data-card-media-track]")) {
      loadCardEnhancements().catch(() => undefined);
    }
  };

  ["pointerover", "pointerdown", "touchstart", "focusin"].forEach(
    (eventName) => {
      document.addEventListener(eventName, loadFromCardInteraction, {
        capture: true,
        passive: true,
      });
    },
  );

  const scheduleIdleLoad = () => {
    const run = () => {
      promoteCardSwatches();
      loadCardEnhancements().catch(() => undefined);
    };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      window.setTimeout(run, 800);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      promoteCardSwatches();
      scheduleIdleLoad();
    });
  } else {
    promoteCardSwatches();
    scheduleIdleLoad();
  }

  document.addEventListener(
    "click",
    (event) => {
      const swatchTarget = event.target.closest(
        ".color-swatcher--wrapper, .color-swatcher--counter",
      );
      if (!swatchTarget || customElements.get("color-swatch")) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (pendingSwatchTarget) return;
      pendingSwatchTarget = swatchTarget;

      loadCardEnhancements()
        .then(() => customElements.whenDefined("color-swatch"))
        .then(() => {
          const target = pendingSwatchTarget;
          pendingSwatchTarget = null;
          if (target?.isConnected) target.click();
        })
        .catch(() => {
          pendingSwatchTarget = null;
        });
    },
    true,
  );
})();
